import React, { useRef, useCallback, useEffect } from 'react';
import { NavItem } from '../../../shared';

import Link from '../../../../Link';
import { Dropdown } from './Dropdown';

import cx from 'classnames';
import { IconChevronDownFilled } from './IconChevronDownFilled';

interface NavigationLinkProps extends NavItem {
  selected: boolean;
  setSelected: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export function NavigationLink({
  name,
  title,
  url,
  links,
  desktopLinks,
  selected,
  setSelected
}: NavigationLinkProps) {
  const navItemRef = useRef<HTMLAnchorElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => () => scheduleClose(0), []);

  const toggleDropdown = () => {
    if (selected) {
      setSelected(undefined);
    } else {
      setSelected(name || title);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleDropdown();
    }
  };

  const scheduleClose = useCallback(
    (delay = 150) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (!delay) {
        timeoutRef.current = null;
        return;
      }
      timeoutRef.current = setTimeout(() => {
        setSelected((current) =>
          current === (name || title) ? undefined : current
        );
        timeoutRef.current = null;
      }, delay);
    },
    [setSelected, name, title]
  );

  const handleMouseEnter = () => {
    scheduleClose(0);
    if (!selected) {
      setSelected(name || title);
    }
  };

  const handleMouseLeave = () => {
    scheduleClose();
  };

  return (
    <ul className="m-0 flex p-0">
      <li className="relative mr-4 mt-0 flex list-none items-center justify-between text-[14px] font-medium">
        {desktopLinks ? (
          <>
            <button
              type="button"
              aria-controls={`nav-expanded-content-${name}`}
              aria-expanded={selected}
              onClick={toggleDropdown}
              onKeyDown={handleKeyDown}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className="relative flex w-full items-center justify-between rounded-md px-2 py-1"
            >
              <span className="!typography-title-4 mr-1 leading-[22px] text-[#1d252c]">
                {name || title}
              </span>
              <div
                className="relative -m-2 flex justify-center p-2"
                data-testid="desktop-mega-navbar"
              >
                <div
                  className={cx(
                    'transition-transform duration-300',
                    'ease-[cubic-bezier(0,0.7,0.3,1)]',
                    selected ? 'rotate-180' : 'rotate-0'
                  )}
                >
                  <IconChevronDownFilled />
                </div>

                <div
                  id="dropdown-caret"
                  className={cx(
                    'absolute top-7 block h-0 w-0 rotate-45 border-[10px] border-r-[5px] border-solid border-white',
                    { hidden: !selected }
                  )}
                  style={{
                    zIndex: 1,
                    boxShadow: '0 0 0 rgba(0, 0, 0, 1)'
                  }}
                />
              </div>
            </button>
            <div
              className="absolute -left-6 top-4 pt-5"
              id={`nav-expanded-content-${name}`}
              onMouseEnter={() => scheduleClose(0)}
              onMouseLeave={() => scheduleClose()}
            >
              <Dropdown
                displayDropdown={selected}
                desktopLinks={desktopLinks}
              />
            </div>
          </>
        ) : links ? (
          <>
            <button
              type="button"
              aria-controls={`nav-expanded-content-${name}`}
              aria-expanded={selected}
              onClick={toggleDropdown}
              onKeyDown={handleKeyDown}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className="relative flex w-full items-center justify-between rounded-md px-2 py-1"
            >
              <span className="!typography-title-4 mr-1 leading-[22px] text-[#1d252c]">
                {name || title}
              </span>
              <div
                className="relative -m-2 flex justify-center p-2"
                data-testid="desktop-mega-navbar-links"
              >
                <div
                  className={cx(
                    'transition-transform duration-300',
                    'ease-[cubic-bezier(0,0.7,0.3,1)]',
                    selected ? 'rotate-180' : 'rotate-0'
                  )}
                >
                  <IconChevronDownFilled />
                </div>

                <div
                  id="dropdown-caret"
                  className={cx(
                    'absolute top-7 block h-0 w-0 rotate-45 border-[10px] border-r-[5px] border-solid border-white',
                    { hidden: !selected }
                  )}
                  style={{
                    zIndex: 1,
                    boxShadow: '0 0 0 rgba(0, 0, 0, 1)'
                  }}
                />
              </div>
            </button>
            <div
              className="absolute -left-1 top-4 pt-5"
              id={`nav-expanded-content-${name}`}
              onMouseEnter={() => scheduleClose(0)}
              onMouseLeave={() => scheduleClose()}
            >
              <Dropdown displayDropdown={selected} desktopLinks={links} />
            </div>
          </>
        ) : (
          <div className="relative flex items-center justify-between rounded-md px-2 py-1">
            <Link
              href={url as string}
              ref={navItemRef}
              className="!typography-title-4 mr-1 leading-[22px] text-[#1d252c] no-underline"
            >
              {name || title}
            </Link>
          </div>
        )}
      </li>
    </ul>
  );
}
